/**
 * Datas e horários dos campos do design system: ISO (`aaaa-mm-dd`, `hh:mm`) por fora e o
 * formato brasileiro (`dd/mm/aaaa`) na tela. Sem domínio — só calendário.
 */

export const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/** Semana começando no domingo, como no calendário brasileiro. */
export const WEEKDAYS = [
  { short: 'D', long: 'domingo' },
  { short: 'S', long: 'segunda' },
  { short: 'T', long: 'terça' },
  { short: 'Q', long: 'quarta' },
  { short: 'Q', long: 'quinta' },
  { short: 'S', long: 'sexta' },
  { short: 'S', long: 'sábado' },
] as const;

const pad = (n: number) => String(n).padStart(2, '0');

export type YMD = { y: number; m: number; d: number };

/** `m` de 0 a 11. Devolve `null` para data que não existe (31/02). */
export function parseIso(iso: string | null | undefined): YMD | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');

  if (!match) return null;

  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const date = new Date(y, m, d);

  return date.getFullYear() === y && date.getMonth() === m && date.getDate() === d ? { y, m, d } : null;
}

export function toIso({ y, m, d }: YMD): string {
  const date = new Date(y, m, d);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayIso(): string {
  const now = new Date();

  return toIso({ y: now.getFullYear(), m: now.getMonth(), d: now.getDate() });
}

export function addDaysIso(iso: string, days: number): string {
  const p = parseIso(iso)!;

  return toIso({ ...p, d: p.d + days });
}

/** Soma meses mantendo o dia quando cabe (31/01 + 1 mês → 28 ou 29/02). */
export function addMonthsIso(iso: string, months: number): string {
  const p = parseIso(iso)!;
  const first = new Date(p.y, p.m + months, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

  return toIso({ y: first.getFullYear(), m: first.getMonth(), d: Math.min(p.d, last) });
}

/** Limita ao intervalo; ISO compara como texto. */
export function clampIso(iso: string, min?: string, max?: string): string {
  if (min && iso < min) return min;
  if (max && iso > max) return max;

  return iso;
}

export function isoToDisplay(iso: string | null | undefined): string {
  const p = parseIso(iso);

  return p ? `${pad(p.d)}/${pad(p.m + 1)}/${p.y}` : '';
}

/** "14 de outubro de 2026" — o nome de cada dia para leitor de tela. */
export function longLabel(iso: string): string {
  const p = parseIso(iso)!;

  return `${p.d} de ${MONTHS[p.m]} de ${p.y}`;
}

/**
 * Máscara enquanto se digita: só os dígitos, com as barras no lugar. Aceita também o ISO
 * colado (`2026-10-14`) e o converte.
 */
export function maskDate(text: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(text.trim())) return isoToDisplay(text.trim()) || text;

  const digits = text.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** `dd/mm/aaaa` completo e válido → ISO; senão `null`. */
export function displayToIso(text: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);

  if (!match) return null;

  const iso = `${match[3]}-${match[2]}-${match[1]}`;

  return parseIso(iso) ? iso : null;
}

/** Máscara do horário: `hhmm` → `hh:mm`. */
export function maskTime(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 4);

  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

export function isValidTime(text: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(text);

  return !!match && Number(match[1]) < 24 && Number(match[2]) < 60;
}

/** As 6 semanas (42 dias) que o mês ocupa na grade, de domingo a sábado. */
export function monthGrid(y: number, m: number): string[] {
  const start = new Date(y, m, 1 - new Date(y, m, 1).getDay());

  return Array.from({ length: 42 }, (_, i) =>
    toIso({ y: start.getFullYear(), m: start.getMonth(), d: start.getDate() + i })
  );
}
